import { useCallback, useContext, useEffect, useState } from 'react'
import { useParams, useNavigate, Navigate } from 'react-router-dom'
import {
  Stack,
  Typography,
  CircularProgress,
  Box,
  Button,
  Alert,
  Snackbar,
} from '@mui/material'

import {
  approve_crediting_upload,
  delete_clothing_item,
  delete_clothes_crediting_upload,
  get_clothes_crediting_upload,
  mark_crediting_upload_as_complete,
  mark_crediting_upload_as_partial,
  replace_clothing_item,
  reject_clothing_item,
  edit_clothes_crediting_upload,
  merge_clothes_crediting_uploads,
} from 'api/clothes_service'
import { ClothingDataDB, CreditingType, UploadStatus } from 'models'
import UploadCard from 'components/upload_card'
import BasicDialog from 'components/basic_dialog'
import ButtonWithAutoLoading from 'components/button_with_auto_loading'
import ClothingItemForm from 'components/clothing_item_form'
import ClothingMergeForm from 'components/clothing_merge_form'
import { useDocumentTitle } from 'hooks'
import ClothingItemAccordion from 'components/clothing_item_accordion'
import { NotFoundError, UnauthorisedError } from 'errors'
import ClothingRejectedItemAccordion from 'components/clothing_rejected_items_accordion'
import { UserContext } from 'base'
import ClothingDataForm from 'components/clothing_data_form'

interface UploadProps {
  uploadStatus: UploadStatus
}

const getUploadRoute = (uploadStatus: UploadStatus) => {
  switch (uploadStatus) {
    case UploadStatus.IN_PROGRESS:
      return '/in-progress'
    case UploadStatus.PARTIAL:
      return '/partial'
    case UploadStatus.IN_REVIEW:
      return `/uploads`
    case UploadStatus.COMPLETED:
      return `/completed`
  }
}

const UploadContent = ({ uploadStatus }: UploadProps) => {
  useDocumentTitle('Review Upload')

  const [clothingData, setClothingData] = useState<ClothingDataDB>()
  const [isMarkCompleteDialogOpen, setIsMarkCompleteDialogOpen] =
    useState<boolean>(false)
  const [isMarkPartialDialogOpen, setIsMarkPartialDialogOpen] =
    useState<boolean>(false)
  const [isApproveDialogOpen, setIsApproveDialogOpen] = useState<boolean>(false)
  const [isEditUploadDialogOpen, setIsEditUploadDialogOpen] =
    useState<boolean>(false)
  const [isDeleteUploadDialogOpen, setIsDeleteUploadDialogOpen] =
    useState<boolean>(false)
  const [isEditDialogOpen, setIsEditDialogOpen] = useState<boolean>(false)
  const [isDeleteItemDialogOpen, setIsDeleteItemDialogOpen] =
    useState<boolean>(false)
  const [isRejectItemDialogOpen, setIsRejectItemDialogOpen] =
    useState<boolean>(false)
  const [isMergeDialogOpen, setIsMergeDialogOpen] = useState<boolean>(false)
  const [selectedIndex, setSelectedIndex] = useState<number>(0)
  const [error, setError] = useState<string>()

  const [showSnackbarError, setShowSnackbarError] = useState<boolean>(false)
  const [snackbarError, setSnackbarError] = useState<string>('')
  const handleSnackbarClose = (
    event?: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === 'clickaway') {
      return
    }

    setShowSnackbarError(false)
  }

  const { id } = useParams()
  const navigate = useNavigate()
  const user = useContext(UserContext)

  const populateClothingUpload = useCallback(async () => {
    setError(undefined)
    try {
      const clothingData = await get_clothes_crediting_upload(id!)
      setClothingData(clothingData)
    } catch (e: any) {
      if (e instanceof UnauthorisedError) {
        return user.setLoggedOut()
      }
      if (e instanceof NotFoundError) {
        setError('Upload not found.')
        return
      }
      setError(e.message)
    }
  }, [id, user])

  useEffect(() => {
    populateClothingUpload()
  }, [
    isEditUploadDialogOpen,
    isEditDialogOpen,
    isDeleteItemDialogOpen,
    isMergeDialogOpen,
    isRejectItemDialogOpen,
    populateClothingUpload,
  ])

  if (id === undefined) return <></>

  const handleEditItem = (i: number) => {
    setSelectedIndex(i)
    setIsEditDialogOpen(true)
  }

  const handleDeleteItem = (i: number) => {
    setSelectedIndex(i)
    setIsDeleteItemDialogOpen(true)
  }

  const handleRejectItem = (i: number) => {
    setSelectedIndex(i)
    setIsRejectItemDialogOpen(true)
  }

  if (error !== undefined) {
    return (
      <Alert severity="error">
        <Typography variant="h5">{error}</Typography>
      </Alert>
    )
  }

  if (clothingData === undefined) {
    return (
      <Box display="flex" justifyContent="center" pt={2}>
        <Stack display="flex" alignItems="center" spacing={1}>
          <CircularProgress />
          <Typography variant="body1">Loading data...</Typography>
        </Stack>
      </Box>
    )
  }

  if (clothingData.status !== uploadStatus) {
    return <Navigate to={`${getUploadRoute(clothingData.status)}/${id}`} />
  }

  return (
    <>
      <UploadCard clothingData={clothingData}>
        {(clothingData.status === UploadStatus.IN_REVIEW ||
          clothingData.status === UploadStatus.PARTIAL) && (
          <Button
            size="small"
            variant="text"
            color="primary"
            onClick={() => setIsEditUploadDialogOpen(true)}
          >
            Edit
          </Button>
        )}
      </UploadCard>
      <Stack spacing={2}>
        <Typography variant="h5">Clothing Items</Typography>
        <Stack>
          {clothingData.clothes.length === 0 && <Typography>None.</Typography>}
          {clothingData.clothes.map((clothingItem, i) => (
            <ClothingItemAccordion
              key={clothingItem.sku}
              clothingItem={clothingItem}
              index={i}
              buttons={
                clothingData.status === UploadStatus.IN_REVIEW && (
                  <>
                    <Button
                      variant="outlined"
                      onClick={() => handleEditItem(i)}
                    >
                      Edit
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={() => handleRejectItem(i)}
                    >
                      Reject
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={() => handleDeleteItem(i)}
                    >
                      Delete
                    </Button>
                  </>
                )
              }
            />
          ))}
        </Stack>
      </Stack>
      <Stack spacing={2}>
        <Typography variant="h5">Rejected Items</Typography>
        <Stack>
          {clothingData.rejected_items.length === 0 && (
            <Typography>None.</Typography>
          )}
          {clothingData.rejected_items.map((rejectedItem, i) => (
            <ClothingRejectedItemAccordion
              key={i}
              rejectedItem={rejectedItem}
              index={i}
            />
          ))}
        </Stack>
      </Stack>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Stack spacing={1} direction="row">
          {(clothingData.status === UploadStatus.IN_REVIEW ||
            clothingData.status === UploadStatus.PARTIAL) && (
            <Button
              variant="contained"
              color="info"
              onClick={() => setIsMergeDialogOpen(true)}
            >
              Merge
            </Button>
          )}
          {clothingData.status === UploadStatus.IN_REVIEW && (
            <Button
              variant="contained"
              color="info"
              onClick={() => setIsMarkPartialDialogOpen(true)}
            >
              Mark as Partial
            </Button>
          )}
          {clothingData.status === UploadStatus.PARTIAL && (
            <Button
              variant="contained"
              color="info"
              onClick={() => setIsMarkCompleteDialogOpen(true)}
            >
              Mark as Complete
            </Button>
          )}
        </Stack>
        <Stack spacing={1} direction="row">
          {clothingData.status === UploadStatus.IN_REVIEW && (
            <Button
              variant="contained"
              color="primary"
              onClick={() => setIsApproveDialogOpen(true)}
            >
              Approve
            </Button>
          )}
          {(clothingData.status === UploadStatus.IN_REVIEW ||
            clothingData.status === UploadStatus.PARTIAL) && (
            <Button
              variant="contained"
              color="error"
              onClick={() => setIsDeleteUploadDialogOpen(true)}
            >
              Delete
            </Button>
          )}
        </Stack>
      </Stack>

      <BasicDialog
        open={isApproveDialogOpen}
        onClose={() => setIsApproveDialogOpen(false)}
        title="Approve this upload?"
        actions={
          <ButtonWithAutoLoading
            variant="contained"
            onClick={async () => {
              try {
                await approve_crediting_upload(id)
              } catch (e: any) {
                if (e instanceof UnauthorisedError) {
                  return user.setLoggedOut()
                }
                setSnackbarError(e.message)
                setShowSnackbarError(true)
                return
              }

              setIsApproveDialogOpen(false)
              navigate('/uploads')
            }}
          >
            Yes
          </ButtonWithAutoLoading>
        }
      >
        <Typography variant="body1">
          {clothingData.crediting_type === CreditingType.TRADE_IN ? (
            <>
              Upon approving, an email will be sent to the customer, a gift card
              will be created and sent to them, and the clothing items will be
              queued as Draft warehouse stock at Kian Teck. Prepare and activate
              the listings for online sale.
            </>
          ) : (
            <>
              Upon approving, an email will be sent to the customer and the
              clothing items will be queued as Draft warehouse stock at Kian
              Teck. Prepare and activate the listings for online sale.
            </>
          )}
        </Typography>
      </BasicDialog>

      <BasicDialog
        open={isMarkCompleteDialogOpen}
        onClose={() => setIsMarkCompleteDialogOpen(false)}
        title="Mark this upload as complete?"
        actions={
          <ButtonWithAutoLoading
            variant="contained"
            onClick={async () => {
              try {
                await mark_crediting_upload_as_complete(id)
              } catch (e: any) {
                if (e instanceof UnauthorisedError) {
                  return user.setLoggedOut()
                }
                setSnackbarError(e.message)
                setShowSnackbarError(true)
                return
              }

              setIsMarkCompleteDialogOpen(false)
              navigate('/partial')
            }}
          >
            Yes
          </ButtonWithAutoLoading>
        }
      >
        <Typography variant="body1">
          The upload will still undergo review and require approval.
        </Typography>
      </BasicDialog>

      <BasicDialog
        open={isMarkPartialDialogOpen}
        onClose={() => setIsMarkPartialDialogOpen(false)}
        title="Mark this upload as partial?"
        actions={
          <ButtonWithAutoLoading
            variant="contained"
            onClick={async () => {
              try {
                await mark_crediting_upload_as_partial(id)
              } catch (e: any) {
                if (e instanceof UnauthorisedError) {
                  return user.setLoggedOut()
                }
                setSnackbarError(e.message)
                setShowSnackbarError(true)
                return
              }

              setIsMarkPartialDialogOpen(false)
              navigate('/uploads')
            }}
          >
            Yes
          </ButtonWithAutoLoading>
        }
      >
        <Typography variant="body1">
          Marking as partial will allow <b>new</b> matching uploads to be merged
          with this upload. However, <b>existing</b> partial uploads will{' '}
          <b>not</b> be automatically merged.
        </Typography>
      </BasicDialog>

      <BasicDialog
        open={isMergeDialogOpen}
        onClose={() => setIsMergeDialogOpen(false)}
        title="Merge Uploads"
      >
        <ClothingMergeForm
          clothingData={clothingData}
          onSubmit={async (primaryId, secondaryId) => {
            try {
              await merge_clothes_crediting_uploads(primaryId, secondaryId)
            } catch (e: any) {
              if (e instanceof UnauthorisedError) {
                return user.setLoggedOut()
              }
              setSnackbarError(e.message)
              setShowSnackbarError(true)
              return
            }
            setIsMergeDialogOpen(false)
          }}
        />
      </BasicDialog>

      {clothingData.clothes[selectedIndex] && (
        <BasicDialog
          open={isRejectItemDialogOpen}
          onClose={() => setIsRejectItemDialogOpen(false)}
          title={`Reject this item? (${clothingData.clothes[selectedIndex].sku})`}
          actions={
            <ButtonWithAutoLoading
              variant="contained"
              color="error"
              onClick={async () => {
                try {
                  await reject_clothing_item(
                    clothingData.id,
                    clothingData.clothes[selectedIndex].sku
                  )
                } catch (e: any) {
                  if (e instanceof UnauthorisedError) {
                    return user.setLoggedOut()
                  }
                  setSnackbarError(e.message)
                  setShowSnackbarError(true)
                  return
                }
                setIsRejectItemDialogOpen(false)
              }}
            >
              Yes, Reject
            </ButtonWithAutoLoading>
          }
        >
          <Typography variant="body1">
            This clothing item will be marked as rejected. Please remove the SKU
            label if any. This action is irreversible.
          </Typography>
        </BasicDialog>
      )}
      <BasicDialog
        open={isDeleteUploadDialogOpen}
        onClose={() => setIsDeleteUploadDialogOpen(false)}
        title="Delete this upload?"
        actions={
          <ButtonWithAutoLoading
            variant="contained"
            color="error"
            onClick={async () => {
              try {
                await delete_clothes_crediting_upload(id)
              } catch (e: any) {
                if (e instanceof UnauthorisedError) {
                  return user.setLoggedOut()
                }
                setSnackbarError(e.message)
                setShowSnackbarError(true)
                return
              }
              setIsDeleteUploadDialogOpen(false)
              navigate('/uploads')
            }}
          >
            Yes, Delete
          </ButtonWithAutoLoading>
        }
      >
        <Typography variant="body1">
          All clothing items in this upload and all information associated with
          this upload will be deleted. This action is irreversible.
        </Typography>
      </BasicDialog>

      <BasicDialog
        open={isEditUploadDialogOpen}
        onClose={() => setIsEditUploadDialogOpen(false)}
        title="Edit upload details"
      >
        <ClothingDataForm
          clothingData={clothingData}
          onSubmit={async (clothingData) => {
            try {
              await edit_clothes_crediting_upload(
                clothingData.id,
                clothingData.customer_first_name,
                clothingData.customer_email,
                clothingData.crediting_type,
                clothingData.collection_date
              )
            } catch (e: any) {
              if (e instanceof UnauthorisedError) {
                return user.setLoggedOut()
              }
              setSnackbarError(e.message)
              setShowSnackbarError(true)
              return
            }
            setIsEditUploadDialogOpen(false)
          }}
        ></ClothingDataForm>
      </BasicDialog>

      <BasicDialog
        open={isEditDialogOpen}
        onClose={() => setIsEditDialogOpen(false)}
        title="Edit upload"
      >
        <ClothingItemForm
          clothingItem={clothingData.clothes[selectedIndex]}
          onSubmit={async (clothingItem) => {
            try {
              await replace_clothing_item(
                clothingData.id,
                clothingData.clothes[selectedIndex].sku,
                clothingItem
              )
            } catch (e: any) {
              if (e instanceof UnauthorisedError) {
                return user.setLoggedOut()
              }
              setSnackbarError(e.message)
              setShowSnackbarError(true)
              return
            }
            setIsEditDialogOpen(false)
          }}
        ></ClothingItemForm>
      </BasicDialog>

      {clothingData.clothes[selectedIndex] && (
        <BasicDialog
          open={isDeleteItemDialogOpen}
          onClose={() => setIsDeleteItemDialogOpen(false)}
          title={`Delete this item? (${clothingData.clothes[selectedIndex].sku})`}
          actions={
            <ButtonWithAutoLoading
              variant="contained"
              color="error"
              onClick={async () => {
                try {
                  await delete_clothing_item(
                    clothingData.id,
                    clothingData.clothes[selectedIndex].sku
                  )
                } catch (e: any) {
                  if (e instanceof UnauthorisedError) {
                    return user.setLoggedOut()
                  }
                  setSnackbarError(e.message)
                  setShowSnackbarError(true)
                  return
                }
                setIsDeleteItemDialogOpen(false)
              }}
            >
              Yes, Delete
            </ButtonWithAutoLoading>
          }
        >
          <Typography variant="body1">This action is irreversible.</Typography>
        </BasicDialog>
      )}

      <Snackbar
        open={showSnackbarError}
        autoHideDuration={6000}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        onClose={handleSnackbarClose}
      >
        <Alert
          elevation={3}
          onClose={handleSnackbarClose}
          severity="error"
          sx={{ width: '100%' }}
        >
          <Typography variant="h5">{snackbarError}</Typography>
        </Alert>
      </Snackbar>
    </>
  )
}

const Upload = ({ uploadStatus }: UploadProps) => {
  const getHeader = (uploadStatus: UploadStatus) => {
    switch (uploadStatus) {
      case UploadStatus.IN_PROGRESS:
        return 'In-Progress Upload'
      case UploadStatus.PARTIAL:
        return 'Partial Upload'
      case UploadStatus.IN_REVIEW:
        return 'Review Upload'
      case UploadStatus.COMPLETED:
        return 'Completed Upload'
    }
  }

  return (
    <Stack spacing={4}>
      <Typography variant="h3">{getHeader(uploadStatus)}</Typography>
      <UploadContent uploadStatus={uploadStatus} />
    </Stack>
  )
}

export default Upload
